import { useEffect, useRef, useState } from "react";
import { CalendarDays, Pencil } from "lucide-react";

const formatDisplayDate = (value) => {
  if (!value) return "N/A";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
};

const formatInputDate = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 10);
};

const ExamDateCard = ({ examDate: initialExamDate }) => {
  const [examDate, setExamDate] = useState(initialExamDate ?? null);
  const [tempDate, setTempDate] = useState("");
  const [showPicker, setShowPicker] = useState(false);

  const popupRef = useRef(null);

  useEffect(() => {
    setExamDate(initialExamDate ?? null);
  }, [initialExamDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };

    if (showPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPicker]);

  const openPicker = () => {
    setTempDate(formatInputDate(examDate));
    setShowPicker(true);
  };

  const handleSetDate = async (event) => {
    event.preventDefault();

    if (!tempDate) return;

    try {
      await fetch("/api/user-preferences", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ examDate: tempDate }),
      });

      setExamDate(tempDate);
      setTempDate("");
      setShowPicker(false);
    } catch (err) {
      console.error("Failed to save exam date");
    }
  };

  return (
    <div
      className="group relative min-h-[144px] cursor-pointer rounded-md border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-purple-200 hover:shadow-md"
      onClick={() => !showPicker && openPicker()}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold uppercase text-gray-500">
            Exam Date
          </p>
          <p className="mt-4 text-2xl font-extrabold text-gray-800">
            {formatDisplayDate(examDate)}
          </p>
        </div>
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-slate-100 text-purple-700">
          <CalendarDays className="h-5 w-5" aria-hidden="true" />
        </span>
      </div>

      <button
        type="button"
        aria-label="Edit exam date"
        aria-haspopup="dialog"
        className="absolute bottom-4 right-4 grid h-8 w-8 place-items-center rounded-md text-gray-400 transition hover:bg-purple-50 hover:text-purple-700 group-hover:text-purple-700"
        onClick={(event) => {
          event.stopPropagation();
          openPicker();
        }}
      >
        <Pencil className="h-4 w-4" aria-hidden="true" />
      </button>

      {showPicker && (
        <div
          ref={popupRef}
          className="absolute left-1/2 top-full z-50 mt-3 w-64 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-md border border-slate-200 bg-white p-4 shadow-xl"
          onClick={(event) => event.stopPropagation()}
        >
          <form onSubmit={handleSetDate}>
            <label
              htmlFor="exam-date"
              className="block text-xs font-semibold uppercase text-gray-500"
            >
              Exam date
            </label>

            <input
              id="exam-date"
              type="date"
              value={tempDate}
              onChange={(event) => setTempDate(event.target.value)}
              className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-center text-sm text-gray-700 outline-none transition focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
            />

            <button
              type="submit"
              disabled={!tempDate}
              className="mt-3 w-full rounded-md bg-gradient-to-r from-fuchsia-900 to-fuchsia-700 px-4 py-2.5 text-sm font-semibold text-white transition hover:ring-2 hover:ring-purple-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Set Date
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ExamDateCard;
